import { useEffect, useState } from "react";
import { ChevronLeft, Package, TrendingUp, DollarSign, Clock, CheckCircle2, XCircle, Truck } from "lucide-react";
import { formatCurrency } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { prefetchAdImageUrls } from "@/app/hooks/use-ad-image-url";
import { formatShippingMethodForDisplay, getSalesList, type SaleItemApi } from "@/app/api/auth-client";
import { isStoragePath, normalizeAdImageStoragePath } from "@/app/utils/ad-utils";

export interface Sale {
  id: string;
  adTitle: string;
  adImage: string;
  buyerName: string;
  total: number;
  status: "pending" | "paid" | "shipped" | "delivered" | "cancelled";
  date: Date;
  orderId: string;
  shippingMethod?: string;
  /** When > 1, show "adTitle e mais x produtos". */
  itemsCount?: number;
}

/** Map BFF order_status to Sale status for display. */
function mapOrderStatusToSaleStatus(orderStatus: string): Sale["status"] {
  switch (orderStatus) {
    case "pending_payment":
      return "pending";
    case "paid":
    case "shipping_pending":
      return "paid";
    case "in_delivery":
      return "shipped";
    case "delivered":
      return "delivered";
    case "cancelled":
      return "cancelled";
    default:
      return "paid";
  }
}

function saleItemToSale(item: SaleItemApi): Sale {
  return {
    id: item.id,
    adTitle: item.ad_title || "Anúncio",
    adImage: item.ad_image || "",
    buyerName: item.buyer_name || "Comprador",
    total: (item.total_cents ?? 0) / 100,
    status: mapOrderStatusToSaleStatus(item.order_status),
    date: item.date ? new Date(item.date) : new Date(),
    orderId: item.order_id,
    shippingMethod: item.shipping_method,
    itemsCount: item.items_count,
  };
}

interface SalesPageProps {
  onBack: () => void;
  /** When set, clicking a sale opens order detail (for label, tracking, etc.). */
  onOpenOrderDetail?: (orderId: string) => void;
}

export function SalesPage({ onBack, onOpenOrderDetail }: SalesPageProps) {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSalesList()
      .then((res) => {
        if (cancelled) return;
        const items = res.sales || [];
        const paths = items
          .map((s) => normalizeAdImageStoragePath(s.ad_image ?? ""))
          .filter((p) => isStoragePath(p));
        prefetchAdImageUrls(paths);
        setSales(items.map(saleItemToSale));
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Erro ao carregar vendas";
          setError(msg);
          setSales([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getStatusColor = (status: Sale["status"]) => {
    switch (status) {
      case "delivered":
        return "text-green-600 bg-green-50 dark:bg-green-950/30";
      case "shipped":
        return "text-blue-600 bg-blue-50 dark:bg-blue-950/30";
      case "paid":
        return "text-amber-600 bg-amber-50 dark:bg-amber-950/30";
      case "pending":
        return "text-orange-600 bg-orange-50 dark:bg-orange-950/30";
      case "cancelled":
        return "text-red-600 bg-red-50 dark:bg-red-950/30";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusIcon = (status: Sale["status"]) => {
    switch (status) {
      case "delivered":
        return <CheckCircle2 className="w-4 h-4" />;
      case "shipped":
        return <Truck className="w-4 h-4" />;
      case "paid":
        return <DollarSign className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "cancelled":
        return <XCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status: Sale["status"]) => {
    switch (status) {
      case "delivered":
        return "Entregue";
      case "shipped":
        return "Em Transporte";
      case "paid":
        return "Pago - Aguardando Envio";
      case "pending":
        return "Aguardando Pagamento";
      case "cancelled":
        return "Cancelado";
      default:
        return "Desconhecido";
    }
  };

  const totalRevenue = sales
    .filter(s => s.status !== "cancelled")
    .reduce((sum, sale) => sum + sale.total, 0);
  const completedSales = sales.filter(s => s.status === "delivered").length;

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Minhas Vendas</h1>
              <p className="text-sm text-white/80">
                Acompanhe suas transações
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-lg p-4 text-sm">
            {error}
          </div>
        )}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        )}
        {!loading && !error && (
        <>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Receita Total</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(totalRevenue)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {sales.length} vendas
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Concluídas</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {completedSales}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {sales.filter(s => s.status === "shipped").length} em transporte
            </p>
          </div>
        </div>

        {/* Sales List */}
        <div className="space-y-4">
          <h2 className="font-semibold text-lg">Histórico de Vendas</h2>
          
          {sales.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="mb-2">Nenhuma venda ainda</h3>
              <p className="text-muted-foreground text-sm">
                Suas vendas aparecerão aqui
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sales.map((sale) => (
                <div
                  key={sale.id}
                  className={`bg-card border border-border rounded-lg p-4 ${onOpenOrderDetail ? "cursor-pointer hover:bg-accent/50 transition-colors" : ""}`}
                  onClick={onOpenOrderDetail ? () => onOpenOrderDetail(sale.orderId) : undefined}
                  role={onOpenOrderDetail ? "button" : undefined}
                  tabIndex={onOpenOrderDetail ? 0 : undefined}
                  onKeyDown={onOpenOrderDetail ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenOrderDetail(sale.orderId);
                    }
                  } : undefined}
                >
                  <div className="flex gap-4">
                    <AdImage
                      src={sale.adImage || undefined}
                      alt={sale.adTitle}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm line-clamp-1">
                            {sale.itemsCount != null && sale.itemsCount > 1
                              ? `${sale.adTitle} e mais ${sale.itemsCount - 1} ${sale.itemsCount === 2 ? "produto" : "produtos"}`
                              : sale.adTitle}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            Comprador: {sale.buyerName}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-primary whitespace-nowrap">
                          {formatCurrency(sale.total)}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(sale.status)}`}>
                          {getStatusIcon(sale.status)}
                          <span>{getStatusLabel(sale.status)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          #{sale.orderId}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {sale.date.toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        {formatShippingMethodForDisplay(sale.shippingMethod) && (
                          <span className="flex items-center gap-1 min-w-0 max-w-[55%] justify-end">
                            <Truck className="w-3 h-3 shrink-0" />
                            <span className="truncate text-right">
                              {formatShippingMethodForDisplay(sale.shippingMethod)}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
